/**
 * Tiptap 커스텀 노드: PlaceholderTag
 * @description 텍스트 내에서 #{OrderNum} 같은 플레이스홀더를 카드 형태로 표시한다.
 */

import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { PlaceholderTagView } from "./PlaceholderTagView";

/**
 * PlaceholderTag 노드 옵션
 */
export interface PlaceholderTagOptions {
  HTMLAttributes: Record<string, unknown>;
}

/**
 * Tiptap Commands 타입 확장
 */
declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    placeholderTag: {
      /**
       * 커서 위치에 플레이스홀더 태그 삽입
       */
      insertPlaceholder: (
        placeholder: string,
        label: string,
        color: string,
      ) => ReturnType;
    };
  }
}

/**
 * PlaceholderTag 커스텀 노드
 *
 * @description
 * - inline: 텍스트 중간에 삽입 가능
 * - atom: 분할 불가능한 단위 (커서가 내부로 진입하지 않음)
 * - selectable / draggable: 선택·드래그 가능
 */
export const PlaceholderTagNode = Node.create<PlaceholderTagOptions>({
  name: "placeholderTag",

  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      placeholder: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-placeholder"),
        renderHTML: (attributes) => ({
          "data-placeholder": attributes.placeholder,
        }),
      },
      label: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-label"),
        renderHTML: (attributes) => ({
          "data-label": attributes.label,
        }),
      },
      color: {
        default: "#3B82F6",
        parseHTML: (element) => element.getAttribute("data-color"),
        renderHTML: (attributes) => ({
          "data-color": attributes.color,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "span[data-placeholder-tag]",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(
        { "data-placeholder-tag": "" },
        this.options.HTMLAttributes,
        HTMLAttributes,
      ),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(PlaceholderTagView);
  },

  addCommands() {
    return {
      insertPlaceholder:
        (placeholder: string, label: string, color: string) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: { placeholder, label, color },
          });
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      // Backspace로 앞 태그 삭제
      Backspace: () =>
        this.editor.commands.command(({ state, tr, dispatch }) => {
          const { selection } = state;
          const { empty, anchor } = selection;

          if (!empty) {
            return false;
          }

          const $pos = state.doc.resolve(anchor);
          const nodeBefore = $pos.nodeBefore;

          if (nodeBefore && nodeBefore.type.name === this.name) {
            if (dispatch) {
              tr.delete(anchor - nodeBefore.nodeSize, anchor);
              dispatch(tr);
            }
            return true;
          }

          return false;
        }),
      // Delete로 뒤 태그 삭제
      Delete: () =>
        this.editor.commands.command(({ state, tr, dispatch }) => {
          const { selection } = state;
          const { empty, anchor } = selection;

          if (!empty) {
            return false;
          }

          const $pos = state.doc.resolve(anchor);
          const nodeAfter = $pos.nodeAfter;

          if (nodeAfter && nodeAfter.type.name === this.name) {
            if (dispatch) {
              tr.delete(anchor, anchor + nodeAfter.nodeSize);
              dispatch(tr);
            }
            return true;
          }

          return false;
        }),
    };
  },
});

export default PlaceholderTagNode;
